import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ArrowLeft, Send, MessageSquare, UserCircle, Users, Loader2, AlertCircle, Menu, X, Camera, Download, ZoomIn, Image, ImagePlus, ChevronLeft, ChevronRight } from 'lucide-react';
import type { CurrentPage } from '../App';
import { supabase } from '../lib/supabaseClient';
import { sendMessage, getMessages, subscribeToMessages, unsubscribeFromMessages, type TeamMessage } from '../services/teamChatService';
import { uploadChatImage } from '../services/chatImageService';
import { 
  getTeamMembers, 
  getUnreadCounts, 
  sendPrivateMessage, 
  getConversation, 
  markMessagesAsRead,
  subscribeToPrivateMessages,
  unsubscribeFromPrivateMessages,
  type TeamMember,
  type PrivateMessage 
} from '../services/privateChatService';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface TeamChatWithSidebarProps {
  onNavigate: (page: CurrentPage) => void;
}

type ChatMode = 'team' | 'private';

const TeamChatWithSidebar: React.FC<TeamChatWithSidebarProps> = ({ onNavigate }) => {
  // Scroll to top when page loads
  React.useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [chatMode, setChatMode] = useState<ChatMode>('team');
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  
  // Team Chat State
  const [teamMessages, setTeamMessages] = useState<TeamMessage[]>([]);
  
  // Private Chat State
  const [privateMessages, setPrivateMessages] = useState<PrivateMessage[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  
  // Common State
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capturingImage, setCapturingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImages, setPendingImages] = useState<string[]>([]); // For iMessage-style preview
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const [enlargedImageGallery, setEnlargedImageGallery] = useState<string[]>([]); // For navigation
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true); // Hide content until first messages load
  
  // Refs
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const teamChannelRef = useRef<RealtimeChannel | null>(null);
  const privateChannelRef = useRef<RealtimeChannel | null>(null);

  // Get current user ID
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    };
    getCurrentUser();
  }, []);

  // Load team members and unread counts
  useEffect(() => {
    const loadTeamData = async () => {
      try {
        console.log('[TeamChat] Loading team members...');
        const [members, counts] = await Promise.all([
          getTeamMembers(),
          getUnreadCounts()
        ]);
        
        console.log('[TeamChat] Team members loaded:', members.length);
        
        // Add unread counts to members
        const membersWithCounts = members.map(member => ({
          ...member,
          unread_count: counts[member.user_id] || 0
        }));
        
        setTeamMembers(membersWithCounts);
        setUnreadCounts(counts);
      } catch (err) {
        console.error('[TeamChat] Failed to load team data:', err);
        setError('Failed to load team members. Please try refreshing.');
      }
    };
    
    loadTeamData();
  }, []);

  // Load messages based on chat mode
  useEffect(() => {
    const loadMessages = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (chatMode === 'team') {
          // Load team messages
          const history = await getMessages(100);
          setTeamMessages(history);
          
          // Set up team subscription
          const channel = await subscribeToMessages(
            (newMessage) => {
              setTeamMessages(prev => {
                const exists = prev.some(m => m.id === newMessage.id);
                if (exists) return prev;
                return [...prev, newMessage];
              });
            },
            (error) => {
              console.error('[TeamChat] Team subscription error:', error);
            }
          );
          
          if (channel) {
            teamChannelRef.current = channel;
          }
        } else if (chatMode === 'private' && selectedMember) {
          // Load private conversation
          const conversation = await getConversation(selectedMember.user_id, 100);
          setPrivateMessages(conversation);
          
          // Mark messages as read
          await markMessagesAsRead(selectedMember.user_id);
          
          // Update unread count
          setUnreadCounts(prev => ({
            ...prev,
            [selectedMember.user_id]: 0
          }));
          
          // Set up private subscription
          const channel = await subscribeToPrivateMessages(
            selectedMember.user_id,
            (newMessage) => {
              setPrivateMessages(prev => {
                const exists = prev.some(m => m.id === newMessage.id);
                if (exists) return prev;
                return [...prev, newMessage];
              });
              
              // Mark as read if we're viewing this conversation
              if (newMessage.sender_id === selectedMember.user_id) {
                markMessagesAsRead(selectedMember.user_id);
              }
            },
            (error) => {
              console.error('[TeamChat] Private subscription error:', error);
            }
          );
          
          if (channel) {
            privateChannelRef.current = channel;
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('[TeamChat] Failed to load messages:', err);
        setError('Failed to load messages. Please try again.');
        setLoading(false);
      }
    };
    
    loadMessages();
    
    // Cleanup subscriptions on mode change
    return () => {
      if (teamChannelRef.current) {
        unsubscribeFromMessages(teamChannelRef.current);
      }
      if (privateChannelRef.current) {
        unsubscribeFromPrivateMessages(privateChannelRef.current);
      }
    };
  }, [chatMode, selectedMember]);

  // Immediate scroll on mount to prevent flash - useLayoutEffect runs BEFORE paint
  useLayoutEffect(() => {
    // Set scroll container to bottom IMMEDIATELY before paint
    if (messagesContainerRef.current) {
      console.log('[SCROLL] useLayoutEffect - Setting scrollTop to 999999');
      const scrollHeight = messagesContainerRef.current.scrollHeight;
      const clientHeight = messagesContainerRef.current.clientHeight;
      const maxScroll = scrollHeight - clientHeight;
      messagesContainerRef.current.scrollTop = Math.max(0, maxScroll);
      console.log('[SCROLL] Set scrollTop to:', messagesContainerRef.current.scrollTop, 'maxScroll:', maxScroll);
    }
    
    // Also scroll with scrollIntoView
    if (messagesEndRef.current) {
      console.log('[SCROLL] Initial mount - scrolling immediately to prevent flash');
      messagesEndRef.current.scrollIntoView({ behavior: "instant", block: "end" });
    }
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    console.log('[SCROLL] Messages changed, scheduling scroll...');
    console.log('[SCROLL] Team messages:', teamMessages.length);
    console.log('[SCROLL] Private messages:', privateMessages.length);
    
    // If messages loaded and we're in initial load, mark as loaded
    if (initialLoad && (teamMessages.length > 0 || privateMessages.length > 0)) {
      console.log('[SCROLL] First messages loaded, ending initial load state');
      setInitialLoad(false);
    }
    
    const scrollTimer = setTimeout(() => {
      if (messagesEndRef.current) {
        const scrollContainer = messagesEndRef.current.parentElement;
        console.log('[SCROLL] Executing scroll to bottom (messages changed)');
        console.log('[SCROLL] Before scroll - scrollTop:', scrollContainer?.scrollTop, 'scrollHeight:', scrollContainer?.scrollHeight);
        
        messagesEndRef.current.scrollIntoView({ behavior: "instant", block: "end" });
        
        // Check scroll position after
        setTimeout(() => {
          console.log('[SCROLL] After scroll - scrollTop:', scrollContainer?.scrollTop, 'scrollHeight:', scrollContainer?.scrollHeight);
          console.log('[SCROLL] Scroll complete');
        }, 50);
      } else {
        console.warn('[SCROLL] messagesEndRef.current is null!');
      }
    }, 500);
    return () => {
      console.log('[SCROLL] Cleanup: clearing scroll timer (messages)');
      clearTimeout(scrollTimer);
    };
  }, [teamMessages, privateMessages, initialLoad]);
  
  // Scroll to bottom when switching chats - IMMEDIATELY to prevent flash
  useLayoutEffect(() => {
    console.log('[SCROLL] Chat switched!');
    console.log('[SCROLL] Chat mode:', chatMode);
    console.log('[SCROLL] Selected member:', selectedMember?.full_name || 'none');
    
    // Reset initial load state on chat switch
    setInitialLoad(true);
    
    // Set scroll container to bottom IMMEDIATELY with useLayoutEffect
    if (messagesContainerRef.current) {
      console.log('[SCROLL] useLayoutEffect - Setting scrollTop on chat switch');
      const scrollHeight = messagesContainerRef.current.scrollHeight;
      const clientHeight = messagesContainerRef.current.clientHeight;
      const maxScroll = scrollHeight - clientHeight;
      messagesContainerRef.current.scrollTop = Math.max(0, maxScroll);
      console.log('[SCROLL] Set scrollTop to:', messagesContainerRef.current.scrollTop, 'maxScroll:', maxScroll);
    }
    
    // Also scroll with scrollIntoView
    if (messagesEndRef.current) {
      console.log('[SCROLL] Immediate scroll on chat switch to prevent flash');
      messagesEndRef.current.scrollIntoView({ behavior: "instant", block: "end" });
    }
    
    // Then delayed scroll for when images load
    const scrollTimer = setTimeout(() => {
      if (messagesEndRef.current) {
        const scrollContainer = messagesEndRef.current.parentElement;
        console.log('[SCROLL] Delayed scroll to bottom (chat switch, images loaded)');
        console.log('[SCROLL] Before scroll - scrollTop:', scrollContainer?.scrollTop, 'scrollHeight:', scrollContainer?.scrollHeight);
        
        messagesEndRef.current.scrollIntoView({ behavior: "instant", block: "end" });
        
        // Check scroll position after
        setTimeout(() => {
          console.log('[SCROLL] After scroll - scrollTop:', scrollContainer?.scrollTop, 'scrollHeight:', scrollContainer?.scrollHeight);
          console.log('[SCROLL] Scroll complete');
        }, 50);
      } else {
        console.warn('[SCROLL] messagesEndRef.current is null on chat switch!');
      }
    }, 600);
    return () => {
      console.log('[SCROLL] Cleanup: clearing scroll timer (chat switch)');
      clearTimeout(scrollTimer);
    };
  }, [chatMode, selectedMember]);

  const handleSendMessage = async () => {
    // Allow sending if there's text or pending images
    if (inputText.trim() === '' && pendingImages.length === 0) return;
    if (sending || uploadingImage) return;

    try {
      setSending(true);
      setUploadingImage(true);
      
      // Upload pending images first (with compression)
      const uploadedUrls: string[] = [];
      if (pendingImages.length > 0) {
        console.log(`[TeamChat] 📤 Starting upload for ${pendingImages.length} image(s)...`);
        
        for (let i = 0; i < pendingImages.length; i++) {
          const dataUrl = pendingImages[i];
          console.log(`[TeamChat] 📤 Uploading image ${i + 1}/${pendingImages.length}...`);
          
          try {
            // Upload with compression (handled by uploadChatImage service)
            const imageUrl = await uploadChatImage(dataUrl);
            uploadedUrls.push(imageUrl);
            console.log(`[TeamChat] ✅ Image ${i + 1}/${pendingImages.length} uploaded:`, imageUrl);
          } catch (uploadError) {
            console.error(`[TeamChat] ❌ Failed to upload image ${i + 1}:`, uploadError);
            setError('Failed to upload image. Please try again.');
            setSending(false);
            setUploadingImage(false);
            return;
          }
        }
        
        console.log('[TeamChat] ✅ All images uploaded successfully');
      }
      setError(null);
      
      // Prepare message content and image
      const messageText = inputText.trim();
      let imageUrl: string | undefined;
      let contentWithImages = messageText;
      
      if (uploadedUrls.length > 0) {
        // Use first image as the main image_url
        imageUrl = uploadedUrls[0];
        
        // If multiple images, store all URLs in content (for gallery)
        if (uploadedUrls.length > 1) {
          contentWithImages = uploadedUrls.join(',');
        }
      }
      
      if (chatMode === 'team') {
        // Send team message
        const newMessage = await sendMessage(
          uploadedUrls.length > 1 ? contentWithImages : messageText,
          imageUrl
        );
        setTeamMessages(prev => [...prev, newMessage]);
      } else if (selectedMember) {
        // Send private message
        const newMessage = await sendPrivateMessage(
          selectedMember.user_id,
          selectedMember.full_name,
          uploadedUrls.length > 1 ? contentWithImages : messageText,
          imageUrl
        );
        setPrivateMessages(prev => [...prev, newMessage]);
      }
      
      setInputText('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = '42px';
      }
      // Clear pending images after successful send
      console.log('[IMAGE_SEND] Clearing pending images and states');
      setPendingImages([]);
      setSending(false);
      setUploadingImage(false);
      
      // Scroll to bottom after sending - with extensive logging
      const hasImages = uploadedUrls.length > 0;
      const scrollDelay = hasImages ? 800 : 500; // Longer delay for images
      
      console.log('[SCROLL] Message sent with', uploadedUrls.length, 'images');
      console.log('[SCROLL] Scheduling scroll in', scrollDelay, 'ms');
      
      setTimeout(() => {
        if (messagesEndRef.current) {
          const scrollContainer = messagesEndRef.current.parentElement;
          console.log('[SCROLL] Executing scroll after message send');
          console.log('[SCROLL] Has images:', hasImages);
          console.log('[SCROLL] Before scroll - scrollTop:', scrollContainer?.scrollTop, 'scrollHeight:', scrollContainer?.scrollHeight);
          
          messagesEndRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
          
          // Check multiple times to ensure image rendered
          setTimeout(() => {
            console.log('[SCROLL] Check 1 - scrollTop:', scrollContainer?.scrollTop, 'scrollHeight:', scrollContainer?.scrollHeight);
            messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
          }, 100);
          
          setTimeout(() => {
            console.log('[SCROLL] Check 2 - scrollTop:', scrollContainer?.scrollTop, 'scrollHeight:', scrollContainer?.scrollHeight);
            messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
          }, 300);
          
          setTimeout(() => {
            console.log('[SCROLL] Final check - scrollTop:', scrollContainer?.scrollTop, 'scrollHeight:', scrollContainer?.scrollHeight);
            console.log('[SCROLL] Post-send scroll complete');
          }, 500);
        } else {
          console.warn('[SCROLL] messagesEndRef.current is null after send!');
        }
      }, scrollDelay);
    } catch (err) {
      console.error('[TeamChat] Failed to send message:', err);
      setError('Failed to send message. Please try again.');
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCameraCapture = async () => {
    try {
      setCapturingImage(true);
      setError(null);
      
      console.log('[IMAGE_CAPTURE] 📷 Opening camera...');
      
      // Import Camera dynamically to avoid build issues
      const { Camera: CameraPlugin, CameraResultType, CameraSource } = await import('@capacitor/camera');
      
      // Check and request permissions
      const permission = await CameraPlugin.checkPermissions();
      console.log('[IMAGE_CAPTURE] 📋 Camera permissions:', permission);
      
      if (permission.camera !== 'granted') {
        console.log('[IMAGE_CAPTURE] 🔐 Requesting camera permissions...');
        const requested = await CameraPlugin.requestPermissions();
        
        if (requested.camera !== 'granted') {
          setError('Camera permission denied. Please enable camera access in settings.');
          return;
        }
      }
      
      // Take photo with camera
      console.log('[IMAGE_CAPTURE] 📸 Taking photo...');
      const image = await CameraPlugin.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        saveToGallery: false
      });
      
      if (!image.dataUrl) {
        throw new Error('No image data returned from camera');
      }
      
      console.log('[IMAGE_CAPTURE] ✅ Image captured');
      console.log('[IMAGE_CAPTURE] Data URL length:', image.dataUrl.length);
      console.log('[IMAGE_CAPTURE] Adding to preview array');
      
      // Add to pending images for preview (don't upload yet)
      setPendingImages(prev => {
        const updated = [...prev, image.dataUrl!];
        console.log('[IMAGE_CAPTURE] Total pending images:', updated.length);
        return updated;
      });
      setCapturingImage(false);
      console.log('[IMAGE_CAPTURE] Preview should now be visible');
    } catch (error: any) {
      console.error('[TeamChat] ❌ Failed to capture image:', error);
      
      // Better error messages
      if (error?.message?.includes('User cancelled')) {
        console.log('[TeamChat] ℹ️ User cancelled camera');
        // Don't show error if user cancelled
      } else if (error?.message?.includes('permission')) {
        setError('Camera permission required. Please enable in settings.');
      } else {
        setError('Failed to send image. Please try again.');
      }
    } finally {
      setCapturingImage(false);
      setUploadingImage(false);
    }
  };

  const handleGalleryUpload = async () => {
    try {
      setCapturingImage(true);
      setError(null);
      
      console.log('[TeamChat] 🖼️ Opening gallery...');
      
      // Import Camera dynamically to avoid build issues
      const { Camera: CameraPlugin, CameraResultType, CameraSource } = await import('@capacitor/camera');
      
      // Check and request permissions
      const permission = await CameraPlugin.checkPermissions();
      console.log('[TeamChat] 📋 Gallery permissions:', permission);
      
      if (permission.photos !== 'granted') {
        console.log('[TeamChat] 🔐 Requesting photos permissions...');
        const requested = await CameraPlugin.requestPermissions();
        
        if (requested.photos !== 'granted') {
          setError('Photo library permission denied. Please enable in settings.');
          return;
        }
      }
      
      // Pick photos from gallery (limit to 10)
      console.log('[TeamChat] 📸 Selecting photos...');
      const images = await CameraPlugin.pickImages({
        quality: 90,
        limit: 10
      });
      
      if (!images.photos || images.photos.length === 0) {
        console.log('[TeamChat] ℹ️ No photos selected');
        return;
      }
      
      console.log(`[TeamChat] ✅ ${images.photos.length} image(s) selected, adding to preview`);
      
      // Convert all photos to data URLs and add to pending
      const dataUrls: string[] = [];
      for (const photo of images.photos) {
        if (!photo.webPath) continue;
        
        try {
          const response = await fetch(photo.webPath);
          const blob = await response.blob();
          const dataUrl = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          dataUrls.push(dataUrl);
        } catch (error) {
          console.error('[TeamChat] ❌ Failed to convert photo:', error);
        }
      }
      
      // Add to pending images for preview (don't upload yet)
      setPendingImages(prev => [...prev, ...dataUrls]);
      setCapturingImage(false);
    } catch (error: any) {
      console.error('[TeamChat] ❌ Failed to upload from gallery:', error);
      setPendingImages([]);
      
      if (error?.message?.includes('User cancelled')) {
        console.log('[TeamChat] ℹ️ User cancelled gallery');
      } else if (error?.message?.includes('permission')) {
        setError('Photo library permission required. Please enable in settings.');
      } else {
        setError('Failed to upload photos. Please try again.');
      }
    } finally {
      setCapturingImage(false);
      setUploadingImage(false);
    }
  };

  const handleMemberClick = (member: TeamMember) => {
    setSelectedMember(member);
    setChatMode('private');
    // Only close sidebar on mobile screens (< lg breakpoint = 1024px)
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const handleTeamChatClick = () => {
    setSelectedMember(null);
    setChatMode('team');
    // Only close sidebar on mobile screens (< lg breakpoint = 1024px)
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Determine which messages to display
  const currentMessages = chatMode === 'team' ? teamMessages : privateMessages;
  const isOwnMessage = (msg: TeamMessage | PrivateMessage) => {
    if (chatMode === 'team') {
      return (msg as TeamMessage).user_id === currentUserId;
    } else {
      return (msg as PrivateMessage).sender_id === currentUserId;
    }
  };

  // Download image to device
  const handleDownloadImage = async (imageUrl: string) => {
    try {
      console.log('[TeamChat] 💾 Downloading image:', imageUrl);
      
      // Fetch image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Check if we're on mobile (Capacitor)
      const { Capacitor } = await import('@capacitor/core');
      
      if (Capacitor.isNativePlatform()) {
        // Mobile: Save to photo gallery (works for iOS and Android)
        try {
          // Try using Camera plugin to save to gallery
          const { Camera: CameraPlugin } = await import('@capacitor/camera');
          
          // Convert blob to base64
          const base64Data = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = (reader.result as string);
              resolve(base64);
            };
            reader.readAsDataURL(blob);
          });
          
          // Save to gallery using Filesystem
          const { Filesystem, Directory } = await import('@capacitor/filesystem');
          const fileName = `chat-image-${Date.now()}.jpg`;
          
          // Write file to Cache directory (accessible by photo library on iOS)
          const result = await Filesystem.writeFile({
            path: fileName,
            data: base64Data.split(',')[1],
            directory: Directory.Cache
          });
          
          console.log('[TeamChat] ✅ Image saved to photo gallery');
          alert('Image saved to photo gallery!');
        } catch (saveError) {
          console.error('[TeamChat] ⚠️ Gallery save failed, trying Documents:', saveError);
          
          // Fallback to Documents directory
          const { Filesystem, Directory } = await import('@capacitor/filesystem');
          const base64Data = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = (reader.result as string).split(',')[1];
              resolve(base64);
            };
            reader.readAsDataURL(blob);
          });
          
          const fileName = `chat-image-${Date.now()}.webp`;
          const result = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.Documents
          });
          
          console.log('[TeamChat] ✅ Image saved to Documents:', result.uri);
          alert(`Image saved to Documents folder`);
        }
      } else {
        // Web: Use download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `chat-image-${Date.now()}.webp`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('[TeamChat] ✅ Image downloaded');
      }
    } catch (error) {
      console.error('[TeamChat] ❌ Failed to download image:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-blue-50 via-gray-50 to-indigo-50" style={{ paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}>
      {/* Sidebar - Fixed on mobile for full width */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed lg:relative w-64 h-full transition-all duration-300 bg-white border-r border-gray-200 flex flex-col overflow-hidden z-50 lg:z-auto`}>
        {/* Sidebar Header */}
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 flex justify-between items-center" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: '1rem' }}>
          <h2 className="text-white font-bold text-lg">Team</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white hover:bg-white/10 rounded-full p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Team Chat Button */}
        <button
          onClick={handleTeamChatClick}
          className={`p-3 flex items-center gap-2 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
            chatMode === 'team' ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
          }`}
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <div className="font-semibold text-gray-900 text-sm truncate">Team Chat</div>
            <div className="text-xs text-gray-500 truncate">Organization-wide</div>
          </div>
        </button>

        {/* Team Members List */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Direct Messages
          </div>
          {teamMembers.length === 0 && (
            <div className="p-3 text-sm text-gray-500 text-center">
              No team members found
            </div>
          )}
          {teamMembers.map((member) => (
            <button
              key={member.user_id}
              onClick={() => handleMemberClick(member)}
              className={`w-full p-3 flex items-center gap-2 hover:bg-gray-50 transition-colors ${
                selectedMember?.user_id === member.user_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-semibold">{getInitials(member.full_name)}</span>
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="font-medium text-gray-900 text-sm truncate">{member.full_name}</div>
                <div className="text-xs text-gray-500 truncate">{member.role}</div>
              </div>
              {member.unread_count! > 0 && (
                <div className="bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {member.unread_count}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Backdrop overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Header */}
        <header className="bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600 shadow-xl" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: '1rem' }}>
          <div className="container mx-auto px-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-white hover:bg-white/10 rounded-full p-2"
              >
                <Menu size={22} />
              </button>
              <button
                onClick={() => onNavigate({ page: 'chatHub' })}
                className="text-white hover:bg-white/10 rounded-full p-2"
              >
                <ArrowLeft size={22} />
              </button>
              <div className="flex items-center space-x-3">
                {chatMode === 'team' ? (
                  <>
                    <div className="bg-white/20 rounded-full p-2">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-white">Team Chat</h1>
                      <p className="text-blue-100 text-sm">Organization-wide</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-white/20 rounded-full p-2">
                      <UserCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-white">{selectedMember?.full_name}</h1>
                      <p className="text-blue-100 text-sm">Private chat</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Messages */}
        <main 
          ref={(el) => {
            messagesContainerRef.current = el;
            // Set scrollTop immediately when ref is assigned
            if (el) {
              console.log('[SCROLL] Ref callback - setting initial scrollTop');
              requestAnimationFrame(() => {
                const scrollHeight = el.scrollHeight;
                const clientHeight = el.clientHeight;
                const maxScroll = scrollHeight - clientHeight;
                el.scrollTop = Math.max(0, maxScroll);
                console.log('[SCROLL] Ref callback scrollTop set to:', el.scrollTop);
              });
            }
          }}
          className="flex-1 overflow-y-auto p-4 space-y-6"
          style={{ opacity: initialLoad ? 0 : 1, transition: 'opacity 0.15s ease-in' }}
        >
          {loading && (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          )}
          
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
              <AlertCircle size={20} />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          {!loading && currentMessages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg font-medium">No messages yet</p>
              <p className="text-gray-400 text-sm mt-2">Be the first to start the conversation!</p>
            </div>
          )}
          
          {!loading && currentMessages.map((msg) => {
            const isOwn = isOwnMessage(msg);
            const senderName = chatMode === 'team' 
              ? (msg as TeamMessage).sender_name 
              : (msg as PrivateMessage).sender_name;
            const initials = getInitials(senderName);
            
            return (
              <div
                key={msg.id}
                className={`flex items-end space-x-3 ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                {!isOwn && (
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg border-2 border-white/20">
                    <span className="text-white text-xs font-semibold">{initials}</span>
                  </div>
                )}
                <div className="flex flex-col space-y-1">
                  {!isOwn && (
                    <span className="text-xs text-gray-600 font-medium ml-2">{senderName}</span>
                  )}
                  <div
                    className={`max-w-xs lg:max-w-md px-5 py-3.5 rounded-2xl shadow-lg backdrop-blur-sm border ${
                      isOwn
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-br-md border-blue-400/50'
                        : 'bg-white/95 text-gray-800 rounded-bl-md border-gray-200/50'
                    }`}
                  >
                    {msg.content && !msg.content.includes('http') && (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>
                    )}
                    {msg.image_url && (() => {
                      // Check if content has comma-separated URLs (gallery)
                      const imageUrls = msg.content && msg.content.includes('http') && msg.content.includes(',')
                        ? msg.content.split(',').filter(url => url.trim().startsWith('http'))
                        : [msg.image_url];
                      const imageCount = imageUrls.length;
                      
                      return (
                        <div 
                          className={`relative group cursor-pointer ${msg.content && !msg.content.includes('http') ? 'mt-3' : ''}`}
                          onClick={() => {
                            console.log('[TeamChat] 🔍 Enlarging image(s):', imageUrls);
                            setEnlargedImageGallery(imageUrls);
                            setEnlargedImage(imageUrls[0]);
                            setCurrentImageIndex(0);
                          }}
                        >
                          <img 
                            src={msg.image_url}
                            alt="Shared image"
                            className="rounded-lg max-w-full h-auto hover:opacity-90 transition-opacity shadow-md"
                            style={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'cover' }}
                            loading="lazy"
                            onError={(e) => {
                              console.error('[TeamChat] Failed to load image:', msg.image_url);
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                          {imageCount > 1 && (
                            <div className="absolute top-2 right-2 bg-black/70 text-white text-xs font-bold px-2 py-1 rounded-full">
                              +{imageCount}
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg pointer-events-none">
                            <ZoomIn className="w-6 h-6 text-white drop-shadow-lg" />
                          </div>
                        </div>
                      );
                    })()}
                    <p
                      className={`text-xs mt-2 ${
                        isOwn ? 'text-blue-100 text-right' : 'text-gray-500 text-left'
                      }`}
                    >
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
                {isOwn && (
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center shadow-lg border-2 border-white/20">
                    <UserCircle className="w-5 h-5 text-white" />
                  </div>
                )}
              </div>
            );
          })}
          
          <div ref={messagesEndRef} />
        </main>

        {/* Input */}
        <footer className="bg-white/95 backdrop-blur-sm border-t border-gray-200/50 p-3">
          {/* Image Preview Section (like iMessage/WhatsApp) - Improved UI */}
          {pendingImages.length > 0 && (
            <div className="mb-3 max-h-48 overflow-y-auto pt-3 pb-2">
              <div className="flex items-start gap-2 overflow-x-auto">
                {pendingImages.map((imageUrl, index) => (
                  <div key={index} className="relative flex-shrink-0">
                    <img 
                      src={imageUrl}
                      alt={`Preview ${index + 1}`}
                      className="w-28 h-28 object-cover rounded-xl border-2 border-blue-200 shadow-md"
                      onLoad={() => {
                        console.log('[IMAGE_PREVIEW] Image', index + 1, 'loaded in preview');
                      }}
                    />
                    {/* Remove button - positioned lower to prevent cutoff */}
                    <button
                      onClick={() => {
                        console.log('[IMAGE_PREVIEW] Removing image', index + 1);
                        setPendingImages(prev => prev.filter((_, i) => i !== index));
                      }}
                      className="absolute -top-1 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors z-20 border-2 border-white"
                      title="Remove image"
                    >
                      <X size={16} strokeWidth={2.5} />
                    </button>
                    {uploadingImage && (
                      <div className="absolute inset-0 bg-black/60 rounded-xl flex items-center justify-center">
                        <Loader2 size={24} className="text-white animate-spin" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex items-end space-x-2">
            <div className="flex-grow relative">
              <textarea
                ref={textareaRef}
                value={inputText}
                onChange={(e) => {
                  setInputText(e.target.value);
                  // Auto-resize with discrete line heights
                  requestAnimationFrame(() => {
                    if (textareaRef.current) {
                      // Calculate number of lines (approximate)
                      const lineHeight = 24; // approximate line height
                      const padding = 20; // top + bottom padding
                      textareaRef.current.style.height = '42px';
                      const scrollHeight = textareaRef.current.scrollHeight;
                      const lines = Math.ceil((scrollHeight - padding) / lineHeight);
                      const discreteHeight = Math.min(42 + (lines - 1) * lineHeight, 120);
                      textareaRef.current.style.height = discreteHeight + 'px';
                    }
                  });
                }}
                onKeyDown={(e) => {
                  // Send on Enter, new line on Shift+Enter
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                onKeyUp={(e) => {
                  // Recalculate height on key up (for deletion) - discrete jumps
                  if (textareaRef.current) {
                    const lineHeight = 24;
                    const padding = 20;
                    textareaRef.current.style.height = '42px';
                    const scrollHeight = textareaRef.current.scrollHeight;
                    const lines = Math.ceil((scrollHeight - padding) / lineHeight);
                    const discreteHeight = Math.min(42 + (lines - 1) * lineHeight, 120);
                    textareaRef.current.style.height = discreteHeight + 'px';
                  }
                }}
                placeholder={chatMode === 'team' ? 'Message your team...' : `Message ${selectedMember?.full_name}...`}
                rows={1}
                className="w-full px-4 py-2.5 pr-10 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400 bg-white/50 backdrop-blur-sm text-gray-900 placeholder-gray-500 shadow-sm resize-none overflow-y-auto"
                style={{
                  minHeight: '42px',
                  maxHeight: '120px', // ~5 lines
                  transition: 'none', // Remove smooth transition for discrete jumps
                }}
              />
              <div className="absolute right-2.5 bottom-2.5 pointer-events-none">
                <MessageSquare className="w-4 h-4 text-gray-400" />
              </div>
            </div>
            <button
              onClick={handleGalleryUpload}
              disabled={capturingImage || uploadingImage || sending}
              className={`p-2.5 rounded-xl transition-all duration-200 shadow-md flex-shrink-0 ${
                !capturingImage && !uploadingImage && !sending
                  ? 'text-white bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 hover:shadow-lg'
                  : 'text-gray-400 bg-gray-200 cursor-not-allowed'
              }`}
              title="Upload photos (up to 10)"
            >
              {uploadingImage ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            </button>
            <button
              onClick={handleCameraCapture}
              disabled={capturingImage || uploadingImage || sending}
              className={`p-2.5 rounded-xl transition-all duration-200 shadow-md flex-shrink-0 ${
                !capturingImage && !uploadingImage && !sending
                  ? 'text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 hover:shadow-lg'
                  : 'text-gray-400 bg-gray-200 cursor-not-allowed'
              }`}
              title="Take photo"
            >
              {capturingImage ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
            </button>
            <button
              onClick={handleSendMessage}
              disabled={(inputText.trim() === '' && pendingImages.length === 0) || sending}
              className={`p-2.5 rounded-xl transition-all duration-200 shadow-md flex-shrink-0 ${
                (inputText.trim() || pendingImages.length > 0) && !sending
                  ? 'text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg'
                  : 'text-gray-400 bg-gray-200 cursor-not-allowed'
              }`}
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </footer>
      </div>

      {/* Image Modal */}
      {enlargedImage && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm animate-fadeIn"
          style={{ 
            paddingTop: 'max(1rem, env(safe-area-inset-top))',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom))'
          }}
          onClick={() => {
            console.log('[TeamChat] 🔽 Closing enlarged image');
            setEnlargedImage(null);
            setEnlargedImageGallery([]);
            setCurrentImageIndex(0);
          }}
        >
          {/* Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEnlargedImage(null);
              setEnlargedImageGallery([]);
              setCurrentImageIndex(0);
            }}
            className="absolute right-4 z-10 text-white hover:text-gray-300 transition-colors p-3 rounded-full bg-black/50 hover:bg-black/70"
            style={{ top: 'max(1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
            aria-label="Close"
          >
            <X size={32} />
          </button>
          
          {/* Download Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadImage(enlargedImage);
            }}
            className="absolute right-20 z-10 text-white hover:text-gray-300 transition-colors p-3 rounded-full bg-black/50 hover:bg-black/70"
            style={{ top: 'max(1rem, calc(env(safe-area-inset-top) + 0.5rem))' }}
            title="Download image"
            aria-label="Download"
          >
            <Download size={28} />
          </button>
          
          {/* Gallery Navigation - Previous */}
          {enlargedImageGallery.length > 1 && currentImageIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = currentImageIndex - 1;
                setCurrentImageIndex(newIndex);
                setEnlargedImage(enlargedImageGallery[newIndex]);
              }}
              className="absolute left-4 z-10 text-white hover:text-gray-300 transition-colors p-3 rounded-full bg-black/50 hover:bg-black/70"
              style={{ top: '50%', transform: 'translateY(-50%)' }}
              title="Previous image"
              aria-label="Previous"
            >
              <ChevronLeft size={40} />
            </button>
          )}
          
          {/* Gallery Navigation - Next */}
          {enlargedImageGallery.length > 1 && currentImageIndex < enlargedImageGallery.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newIndex = currentImageIndex + 1;
                setCurrentImageIndex(newIndex);
                setEnlargedImage(enlargedImageGallery[newIndex]);
              }}
              className="absolute right-4 z-10 text-white hover:text-gray-300 transition-colors p-3 rounded-full bg-black/50 hover:bg-black/70"
              style={{ top: '50%', transform: 'translateY(-50%)' }}
              title="Next image"
              aria-label="Next"
            >
              <ChevronRight size={40} />
            </button>
          )}
          
          {/* Image Container */}
          <div 
            className="relative max-w-[95vw] max-h-[95vh] flex flex-col items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={enlargedImage}
              alt="Enlarged image"
              className="max-w-full max-h-full rounded-lg shadow-2xl"
              style={{ objectFit: 'contain' }}
              onError={(e) => {
                console.error('[TeamChat] Failed to load enlarged image');
              }}
            />
            {/* Preload all gallery images (hidden) to eliminate lag */}
            {enlargedImageGallery.length > 1 && enlargedImageGallery.map((url, idx) => (
              idx !== currentImageIndex && (
                <img 
                  key={url} 
                  src={url} 
                  alt="" 
                  style={{ display: 'none' }}
                  loading="eager"
                />
              )
            ))}
            {/* Image counter */}
            {enlargedImageGallery.length > 1 && (
              <div className="mt-4 px-4 py-2 bg-black/70 rounded-full text-white text-sm font-medium">
                {currentImageIndex + 1} / {enlargedImageGallery.length}
              </div>
            )}
          </div>
          
          {/* Hint Text */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white text-sm font-medium bg-black/60 px-6 py-3 rounded-full backdrop-blur-sm">
            Click anywhere to close · Tap download to save
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamChatWithSidebar;
